import type { CollisionContact, CollisionLayer } from './collision.ts';
import { toCompassBearing, toDisplayKmh } from './constants.ts';
import type { MapDocument } from './map/types.ts';
import type { BoatState } from './types.ts';

export const CONTACT_LOG_LIMIT = 50;

/** One play-Log row: the seat that moved, then the contact text. */
export type PlayLogLine = {
  seatId: number;
  label: string;
  text: string;
};

export type ContactReport = {
  layer: CollisionLayer;
  id: string | null;
  /** "shallow sand", "island", "rock", "bounds" */
  source: string;
  solid: boolean;
  damaged: boolean;
  point: { x: number; y: number };
  t: number;
  speed: number;
  heading: number;
  /** Degrees [0, 90] between travel and the crossed shore; null when there is no edge. */
  crossingDeg: number | null;
};

export type HullState = {
  damaged: boolean;
};

export function createHullState(): HullState {
  return { damaged: false };
}

export function applyDamage(
  hull: HullState,
  reports: readonly ContactReport[],
): HullState {
  if (hull.damaged || reports.some((report) => report.damaged)) {
    return { damaged: true };
  }

  return hull;
}

export function reportsFromContacts(
  map: MapDocument,
  contacts: readonly CollisionContact[],
  boat: BoatState,
): ContactReport[] {
  return contacts.map((contact) => ({
    layer: contact.layer,
    id: contact.id,
    source: contactSource(map, contact),
    solid: contact.solid,
    damaged: true,
    point: { x: contact.point.x, y: contact.point.y },
    t: contact.t,
    speed: boat.speed,
    heading: boat.heading,
    crossingDeg: null,
  }));
}

export function formatContactLog(report: ContactReport): string {
  const kmh = Math.round(toDisplayKmh(report.speed));
  const bearing = String(Math.round(toCompassBearing(report.heading)) % 360).padStart(
    3,
    '0',
  );
  const base = `${report.source} · ${kmh} km/h · ${bearing}°`;

  if (report.crossingDeg === null) {
    return base;
  }

  return `${base} · shore ${Math.round(report.crossingDeg)}°`;
}

export function withCrossingAngle(
  reports: readonly ContactReport[],
  crossingDeg: number | null,
): ContactReport[] {
  let attached = false;

  return reports.map((report) => {
    if (attached || !report.solid) {
      return report;
    }

    attached = true;
    return { ...report, crossingDeg };
  });
}

export function appendContactLog(
  lines: readonly PlayLogLine[],
  reports: readonly ContactReport[],
  actor: { seatId: number; label: string },
): PlayLogLine[] {
  if (reports.length === 0) {
    return [...lines];
  }

  return [
    ...lines,
    ...reports.map((report) => ({
      seatId: actor.seatId,
      label: actor.label,
      text: formatContactLog(report),
    })),
  ].slice(-CONTACT_LOG_LIMIT);
}

export function appendPlayLogLines(
  lines: readonly PlayLogLine[],
  next: readonly PlayLogLine[],
): PlayLogLine[] {
  if (next.length === 0) {
    return [...lines];
  }

  return [...lines, ...next].slice(-CONTACT_LOG_LIMIT);
}

function contactSource(map: MapDocument, contact: CollisionContact): string {
  switch (contact.layer) {
    case 'shallow': {
      const zone = map.shallowWater.find((entry) => entry.id === contact.id);
      return zone ? `shallow ${zone.material}` : 'shallow';
    }
    case 'island':
      return 'island';
    case 'rock':
      return 'rock';
    case 'bounds':
      return 'bounds';
  }
}
